import { useMemo } from "react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { useI18n } from "@/contexts/I18nContext";

/**
 * "Cascade de Revenus" — projected revenue impact for a given agent.
 * 12 months area chart, currency-aware via I18n.
 */
export default function AgentRoiChart({ agent, height = 160 }) {
  const { t, formatPrice, currency } = useI18n();

  const data = useMemo(() => {
    const months = t("months");
    // Base monthly leads attributed to this agent + revenue per lead in PHP.
    const base = agent.roi?.baseLeadsPerMonth ?? 50;
    const mult = agent.roi?.multiplier ?? 1.0;
    const revPerLead = 1800; // PHP per lead (conservative, conversion-aware)
    return months.map((m, i) => {
      const grow = Math.pow(1.18, i); // 18% growth per month
      const leads = Math.round((base + 20) * grow * mult);
      const revenuePhp = Math.round(leads * revPerLead);
      return { name: m, revenue: revenuePhp, leads };
    });
  }, [agent, t]);

  const color = agent.color || "#deff9a";
  const gradId = `cascade-${agent.id}`;

  return (
    <div data-testid={`roi-chart-${agent.id}`} className="w-full">
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono-d text-[10px] uppercase tracking-[0.26em] text-white/55">
          {t("cascade_title")}
        </div>
        <div className="font-mono-d text-[10px] uppercase tracking-[0.22em] text-white/45">
          {t("revenue")} · {currency.code}
        </div>
      </div>
      <div style={{ width: "100%", height, minHeight: height }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <defs>
              <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.65} />
                <stop offset="100%" stopColor={color} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis
              dataKey="name"
              stroke="rgba(255,255,255,0.35)"
              tick={{ fontSize: 9, fontFamily: "JetBrains Mono" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis hide />
            <Tooltip
              cursor={{ stroke: color, strokeOpacity: 0.4 }}
              contentStyle={{
                background: "#0a0d10",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8,
                color: "white",
                fontFamily: "Urbanist",
              }}
              formatter={(value, key) => {
                if (key === "revenue") return [formatPrice(value), t("revenue")];
                return [value, key];
              }}
              labelStyle={{ color: "rgba(255,255,255,0.55)", fontSize: 11 }}
            />
            <Area
              type="monotone"
              dataKey="revenue"
              stroke={color}
              strokeWidth={2}
              fill={`url(#${gradId})`}
              animationDuration={650}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
